import { c as _c } from "react/compiler-runtime";
import { cva, type VariantProps } from "class-variance-authority";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import type { UIMessage } from "ai";
import type { ComponentProps, HTMLAttributes } from "react";
export type MessageProps = HTMLAttributes<HTMLDivElement> & {
  from: UIMessage["role"];
};
export const Message = t0 => {
  const $ = _c(10);
  let className;
  let from;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      from,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = from;
    $[3] = props;
  } else {
    className = $[1];
    from = $[2];
    props = $[3];
  }
  const t1 = from === "user" ? "is-user" : "is-assistant flex-row-reverse justify-end";
  let t2;
  if ($[4] !== className || $[5] !== t1) {
    t2 = cn("group flex w-full items-end justify-end gap-2 py-4", t1, className);
    $[4] = className;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== props || $[8] !== t2) {
    t3 = <div className={t2} {...props} />;
    $[7] = props;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};
const messageContentVariants = cva("is-user:dark flex flex-col gap-2 overflow-hidden rounded-xl text-sm", {
  variants: {
    variant: {
      contained: ["max-w-[80%] px-4 py-3", "group-[.is-user]:bg-[#f0f0f0] group-[.is-user]:text-foreground group-[.is-user]:rounded-[20px_20px_4px_20px] group-[.is-user]:animate-[userMsgSlide_0.4s_cubic-bezier(0.22,1,0.36,1)]", "group-[.is-assistant]:bg-transparent group-[.is-assistant]:text-foreground group-[.is-assistant]:animate-[botMsgSlide_0.3s_cubic-bezier(0.16,1,0.3,1)]"],
      flat: ["group-[.is-user]:max-w-[80%] group-[.is-user]:bg-[#f0f0f0] group-[.is-user]:px-4 group-[.is-user]:py-3 group-[.is-user]:text-foreground group-[.is-user]:rounded-[20px_20px_4px_20px]", "group-[.is-assistant]:text-foreground"]
    }
  },
  defaultVariants: {
    variant: "contained"
  }
});
export type MessageContentProps = HTMLAttributes<HTMLDivElement> & VariantProps<typeof messageContentVariants>;
export const MessageContent = t0 => {
  const $ = _c(12);
  let children;
  let className;
  let props;
  let variant;
  if ($[0] !== t0) {
    ({
      children,
      className,
      variant,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = variant;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    variant = $[4];
  }
  let t1;
  if ($[5] !== className || $[6] !== variant) {
    t1 = cn(messageContentVariants({
      variant,
      className
    }));
    $[5] = className;
    $[6] = variant;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== children || $[9] !== props || $[10] !== t1) {
    t2 = <div className={t1} {...props}>{children}</div>;
    $[8] = children;
    $[9] = props;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
};
export type MessageAvatarProps = ComponentProps<typeof Avatar> & {
  src: string;
  name?: string;
};
export const MessageAvatar = t0 => {
  const $ = _c(19);
  let className;
  let name;
  let props;
  let src;
  if ($[0] !== t0) {
    ({
      src,
      name,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = name;
    $[3] = props;
    $[4] = src;
  } else {
    className = $[1];
    name = $[2];
    props = $[3];
    src = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = cn("size-8 ring-1 ring-border", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const t2 = name ?? "User avatar";
  let t3;
  if ($[7] !== src || $[8] !== t2) {
    t3 = <AvatarImage alt={t2} className="mt-0 mb-0" src={src} />;
    $[7] = src;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== name) {
    t4 = name?.slice(0, 2) ?? "ME";
    $[10] = name;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t4) {
    t5 = <AvatarFallback>{t4}</AvatarFallback>;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== props || $[15] !== t1 || $[16] !== t3 || $[17] !== t5) {
    t6 = <Avatar className={t1} {...props}>{t3}{t5}</Avatar>;
    $[14] = props;
    $[15] = t1;
    $[16] = t3;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
};